/**
 * TimelineFilterTypeSection Component
 *
 * Filter buttons for content types (communications, contracts, etc.)
 */

import React, { useCallback } from "react";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Label } from "@/components/ui/label";
import { TYPE_OPTIONS } from "../types/timeline-filters";
import type { TimelineFilters } from "../types/timeline-filters";

interface TimelineFilterTypeSectionProps {
  filters: TimelineFilters;
  onToggleType: (type: string) => void;
}

const TimelineFilterTypeSectionBase: React.FC<
  TimelineFilterTypeSectionProps
> = ({ filters, onToggleType }) => {
  const handleToggle = useCallback(
    (type: string) => {
      onToggleType(type);
    },
    [onToggleType]
  );

  return (
    <div className="space-y-3">
      <div className="flex items-center gap-2">
        <Label className="text-sm font-semibold text-gray-700">
          Content Type
        </Label>
        {filters.type?.length && (
          <Badge variant="secondary" className="text-xs">
            {filters.type.length} selected
          </Badge>
        )}
      </div>
      <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-2">
        {TYPE_OPTIONS.map((type) => {
          const Icon = type.icon;
          const isSelected = filters.type?.includes(type.value);
          return (
            <Button
              key={type.value}
              variant={isSelected ? "default" : "outline"}
              size="sm"
              onClick={() => handleToggle(type.value)}
              className={`h-10 justify-start text-sm font-medium transition-all ${
                isSelected
                  ? "bg-blue-600 text-white shadow-md transform scale-105"
                  : "hover:bg-card-50 hover:border-gray-300"
              }`}
            >
              {Icon && <Icon className="h-4 w-4 mr-2 flex-shrink-0" />}
              <span className="truncate">{type.label}</span>
            </Button>
          );
        })}
      </div>
    </div>
  );
};

export const TimelineFilterTypeSection = React.memo(
  TimelineFilterTypeSectionBase
);
